import React, { useState, useEffect, useCallback, memo } from "react"

import {
  Module
} from "../../Styles"

import { Toggle } from "../Toggle"
import { MessageList } from "../Messaging/MessageList"
import { Controls } from "../Messaging/Controls"
import { ImageUploadModal } from "../Messaging/ImageUploadModal"

import { useCaseViewContext } from '../../context/CaseViewContext'
import { ChatType } from "../Messaging/types"
import { useMessaging } from '../../hooks/useMessaging'
import { useAdminUsers } from '../../hooks/useAdminUsers'
import { useTickets } from '../../hooks/useTickets'
import { API_TYPES, CaseStatusEnum } from '../../constants'
import { CancelledCaseOverlay } from "./styles"

export const Messaging = memo(() => {
  const { caseDetails, apiClasses, apiType, provider, isAssignedProvider, onTicketClosed } = useCaseViewContext()
  
  // Default to ADMIN tab if in admin mode, otherwise default to PATIENT tab
  const [chatContext, setChatContext] = useState(
    apiType === API_TYPES.ADMIN ? ChatType.ADMIN : ChatType.PATIENT
  )
  const [imageUploadVisibility, setImageUploadVisibility] = useState(false);

  const { caseId } = caseDetails || {}

  const [messages, setMessages] = useState({
    loading: false,
    list: [],
  });

  const {
    ticketMessages,
    sendTicketChatMessage,
    markTicketChatAsRead,
    fetchTicketMessages,
    ticket,
    closeTicket,
  } = useTickets({
    caseId,
    apiClasses,
    provider,
    apiType
  })

  const {
    patientMessages,
    sendPatientMessage,
    markPatientChatAsRead,
    fetchPatientMessageList,
  } = useMessaging({
    caseId,
    apiClasses
  })

  const { adminUsers } = useAdminUsers({ apiClasses });

  const handleCloseTicket = useCallback(async () => {
    const success = await closeTicket();
    if (success) onTicketClosed?.();
  }, [closeTicket, onTicketClosed]);

  useEffect(() => {
    const handleRefreshPatientMessages = (event) => {
      if (event.detail?.caseId === caseId) {
        fetchPatientMessageList()
      }
    }

    const handleRefreshTicketMessages = (event) => {
      if (event.detail?.caseId === caseId) {
        fetchTicketMessages()
      }
    }

    window.addEventListener('refreshPatientMessages', handleRefreshPatientMessages)
    window.addEventListener('refreshTicketMessages', handleRefreshTicketMessages)
    
    return () => {
      window.removeEventListener('refreshPatientMessages', handleRefreshPatientMessages)
      window.removeEventListener('refreshTicketMessages', handleRefreshTicketMessages)
    }
  }, [caseId, fetchPatientMessageList, fetchTicketMessages])

  const handleSendMessage = useCallback((messageText, taggedAdminId = null) => {
    const sendFn = chatContext === ChatType.PATIENT
      ? sendPatientMessage
      : sendTicketChatMessage

    return sendFn(messageText, taggedAdminId)
  }, [chatContext, sendPatientMessage, sendTicketChatMessage])

  const onImageUploadModalVisibilityChange = () => {
    setImageUploadVisibility(prevStatus => !prevStatus);
  }

  const handleMarkTicketAsRead = useCallback(() => {
    if (chatContext === ChatType.ADMIN && ticket) {
      markTicketChatAsRead()
    }
    else if (chatContext === ChatType.PATIENT) {
      markPatientChatAsRead()
    }
  }, [chatContext, ticket, markTicketChatAsRead, markPatientChatAsRead])

  useEffect(() => {
    const MessagesByChatType = {
      [ChatType.PATIENT]: patientMessages || [],
      [ChatType.ADMIN]: ticketMessages || [],
    }
    const currentMessages = MessagesByChatType[chatContext] || []
    setMessages(state => ({ ...state, list: currentMessages }))
  }, [chatContext, patientMessages, ticketMessages])

  return (
    <Module
      width="30%"
      border="none"
      padding="0"
      gap="0"
      style={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <Toggle
          chatContext={chatContext}
          setChatContext={setChatContext}
          caseId={caseDetails?._id}
          handleMarkTicketAsRead={handleMarkTicketAsRead}
          getChatWithUnreadPatientMessagesCount={() => {}}
          caseTicketList={[]}
          chatList={[]}
        />
      </div>
      
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '16px 14px 12px' }}>
        <MessageList
          chatContext={chatContext}
          messages={messages}
          handleMarkTicketAsRead={handleMarkTicketAsRead}
          adminUsers={adminUsers}
        />
        {chatContext === ChatType.PATIENT && caseDetails?.status === CaseStatusEnum.CANCELLED && (
          <CancelledCaseOverlay>
            <div>
              This case has been cancelled
            </div>
          </CancelledCaseOverlay>
        )}
      </div>
      
      <div style={{ flexShrink: 0, borderTop: '1px solid #eee' }}>
        {(
          (chatContext === ChatType.PATIENT && apiType !== API_TYPES.ADMIN && isAssignedProvider) || 
          chatContext === ChatType.ADMIN
        ) && (
          <Controls
            chatContext={chatContext}
            assignedAdmin={ticket?.rep}
            ticketStatus={ticket?.status}
            handleSendMessage={handleSendMessage}
            handleMarkTicketAsRead={handleMarkTicketAsRead}
            showModal={onImageUploadModalVisibilityChange}
            handleCloseTicket={handleCloseTicket}
            isAdmin={apiType === API_TYPES.ADMIN}
            caseStatus={caseDetails?.status}
          />
        )}
      </div>
      
      { imageUploadVisibility && (
        <ImageUploadModal
          visible={imageUploadVisibility}
          onCancel={onImageUploadModalVisibilityChange}
          onOk={onImageUploadModalVisibilityChange}
          chatContext={chatContext}
        /> )
      }
    </Module>
  )
})
